import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Spinner,
  TextInput
} from "@patternfly/react-core";

import { apiGet, apiPost } from "../../app/api";
import { formatNumber, formatQuantity } from "../../display";
import { StandardDataTable, type StandardColumn } from "../../components/StandardDataTable";

type PosMappingRow = {
  id?: number;
  legacy_products_variants_id?: number;
  product?: string;
  product_dbname?: string;
  item?: string;
  size?: string;
  quantity_multiplier?: number;
  quantity_rule?: string;
  base_quantity?: number;
  review_threshold?: number;
  unit?: string;
  descriptor?: string;
  variants?: string;
  normalized_item?: string;
  normalized_variants?: string;
  active?: number;
};

type MappingForm = {
  id: string;
  product: string;
  product_dbname: string;
  item: string;
  size: string;
  quantity_multiplier: string;
  quantity_rule: string;
  base_quantity: string;
  review_threshold: string;
  unit: string;
  descriptor: string;
  variants: string;
  normalized_item: string;
  normalized_variants: string;
  active: string;
};

type MappingPayload = {
  columns?: string[];
  rows?: PosMappingRow[];
  source?: string;
};

const emptyForm: MappingForm = {
  id: "",
  product: "",
  product_dbname: "",
  item: "",
  size: "",
  quantity_multiplier: "1",
  quantity_rule: "multiplier",
  base_quantity: "",
  review_threshold: "0",
  unit: "",
  descriptor: "",
  variants: "",
  normalized_item: "",
  normalized_variants: "",
  active: "1"
};

function formFromRow(row: PosMappingRow): MappingForm {
  return {
    id: String(row.id || ""),
    product: row.product || "",
    product_dbname: row.product_dbname || "",
    item: row.item || "",
    size: row.size || "",
    quantity_multiplier: String(row.quantity_multiplier ?? 1),
    quantity_rule: row.quantity_rule || "multiplier",
    base_quantity: String(row.base_quantity ?? ""),
    review_threshold: String(row.review_threshold ?? 0),
    unit: row.unit || "",
    descriptor: row.descriptor || "",
    variants: row.variants || "",
    normalized_item: row.normalized_item || "",
    normalized_variants: row.normalized_variants || "",
    active: String(Number(row.active ?? 1))
  };
}

function parseCsv(text: string): Record<string, string>[] {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    const next = text[index + 1];
    if (quoted && character === '"' && next === '"') {
      field += '"';
      index += 1;
    } else if (character === '"') {
      quoted = !quoted;
    } else if (!quoted && character === ",") {
      row.push(field);
      field = "";
    } else if (!quoted && (character === "\n" || character === "\r")) {
      if (character === "\r" && next === "\n") index += 1;
      row.push(field);
      field = "";
      if (row.some(value => value.trim())) rows.push(row);
      row = [];
    } else {
      field += character;
    }
  }
  row.push(field);
  if (row.some(value => value.trim())) rows.push(row);
  const headers = (rows.shift() || []).map(value => value.trim());
  if (!headers.length) return [];
  return rows.map(values => {
    const record: Record<string, string> = {};
    headers.forEach((header, index) => {
      record[header] = values[index] || "";
    });
    return record;
  });
}

function mappingSummary(row: PosMappingRow): string {
  const product = row.product || "No product";
  const item = row.item || "No StoreHub item";
  const variants = row.variants ? ` (${row.variants})` : "";
  return `${item}${variants} -> ${product}`;
}

export function PosMappingWorkspace({ csrfToken }: { csrfToken: string }) {
  const [rows, setRows] = useState<PosMappingRow[]>([]);
  const [source, setSource] = useState("");
  const [form, setForm] = useState<MappingForm>(emptyForm);
  const [editing, setEditing] = useState<PosMappingRow | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ variant: "success" | "danger"; title: string } | null>(null);

  const loadMappings = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const payload = await apiGet<MappingPayload>("/api/db-edits/products-and-variants");
      setRows(payload.rows || []);
      setSource(payload.source || "");
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "POS Item Links could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMappings();
  }, [loadMappings]);

  const updateForm = (key: keyof MappingForm, value: string): void => {
    setForm(current => ({ ...current, [key]: value }));
  };

  const openEditor = (row: PosMappingRow): void => {
    setEditing(row);
    setForm(formFromRow(row));
  };

  const closeEditor = (): void => {
    setEditing(null);
    setForm(emptyForm);
  };

  const saveMapping = async (): Promise<void> => {
    if (!form.id) return;
    setSaving(true);
    setMessage(null);
    try {
      await apiPost<{ ok: boolean; id: number }>("/api/db-edits/products-and-variants", {
        ...form,
        id: Number(form.id),
        quantity_multiplier: Number(form.quantity_multiplier || 1),
        base_quantity: form.base_quantity ? Number(form.base_quantity) : "",
        review_threshold: Number(form.review_threshold || 0),
        active: Number(form.active)
      }, csrfToken);
      setMessage({ variant: "success", title: "POS item link saved." });
      closeEditor();
      await loadMappings();
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "POS item link could not be saved." });
    } finally {
      setSaving(false);
    }
  };

  const importMappings = async (): Promise<void> => {
    if (!csvFile) {
      setMessage({ variant: "danger", title: "Choose a CSV file first." });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const parsedRows = parseCsv(await csvFile.text());
      const result = await apiPost<{ ok: boolean; inserted?: number; skipped?: number }>("/api/db-edits/products-and-variants/import", {
        filename: csvFile.name,
        rows: parsedRows
      }, csrfToken);
      setMessage({
        variant: "success",
        title: `Imported ${formatNumber(result.inserted || 0)} rows and skipped ${formatNumber(result.skipped || 0)} rows.`
      });
      setCsvFile(null);
      await loadMappings();
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "POS item links could not be imported." });
    } finally {
      setSaving(false);
    }
  };

  const columns: StandardColumn<PosMappingRow>[] = useMemo(() => [
    {
      key: "item",
      label: "StoreHub Item",
      value: mappingSummary,
      render: row => <><strong>{row.item || ""}</strong><small>{row.variants || row.normalized_item || ""}</small></>
    },
    { key: "product", label: "IMS Product", value: row => row.product || "", render: row => <><strong>{row.product || ""}</strong><small>{row.product_dbname || ""}</small></> },
    { key: "size", label: "Size", value: row => row.size || "" },
    { key: "quantity_multiplier", label: "Multiplier", value: row => row.quantity_multiplier ?? 0, render: row => formatQuantity(row.quantity_multiplier), textCenter: true },
    { key: "quantity_rule", label: "Rule", value: row => row.quantity_rule || "" },
    { key: "base_quantity", label: "Base Qty", value: row => row.base_quantity ?? 0, render: row => formatQuantity(row.base_quantity), textCenter: true },
    { key: "review_threshold", label: "Review Threshold", value: row => row.review_threshold ?? 0, render: row => formatQuantity(row.review_threshold), textCenter: true },
    { key: "unit", label: "Unit", value: row => row.unit || "" },
    { key: "active", label: "Active", value: row => Number(row.active ?? 1) ? "Active" : "Inactive", render: row => <Label color={Number(row.active ?? 1) ? "green" : "grey"}>{Number(row.active ?? 1) ? "Active" : "Inactive"}</Label> },
    { key: "actions", label: "Actions", sortable: false, render: row => <Button variant="link" isInline onClick={() => openEditor(row)}>Edit</Button> }
  ], []);

  return (
    <PageSection className="pf-toj-workspace" hasBodyWrapper={false}>
      <div id="pf-pos-mapping-view">
        <Flex alignItems={{ default: "alignItemsFlexStart" }} justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-workspace-title">
          <FlexItem>
            <h1>POS Item Links</h1>
            <p>Private StoreHub mapping maintenance for matching POS sales to IMS items.</p>
          </FlexItem>
          {loading && <FlexItem><Spinner size="md" aria-label="Loading POS Item Links" /></FlexItem>}
        </Flex>
        {message && <Alert variant={message.variant} title={message.title} isInline className="pf-toj-inline-alert" />}
        <Card className="pf-toj-work-card pf-toj-primary-work-card">
          <CardBody>
            <Flex alignItems={{ default: "alignItemsCenter" }} justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-table-heading">
              <FlexItem>
                <h2>StoreHub Product Links</h2>
                <p className="pf-toj-muted">{source || "storehub_product_mappings"}</p>
              </FlexItem>
              <FlexItem>
                <Flex spaceItems={{ default: "spaceItemsSm" }} alignItems={{ default: "alignItemsCenter" }}>
                  <FlexItem>
                    <input className="pf-toj-file-input pf-toj-compact-file-input" type="file" accept=".csv,text/csv" onChange={event => setCsvFile(event.currentTarget.files?.[0] || null)} />
                  </FlexItem>
                  <FlexItem>
                    <Button variant="secondary" onClick={() => void importMappings()} isDisabled={saving || !csvFile} isLoading={saving}>
                      Import CSV
                    </Button>
                  </FlexItem>
                </Flex>
              </FlexItem>
            </Flex>
            {loading ? <Spinner size="lg" aria-label="Loading POS item links" /> : (
              <StandardDataTable ariaLabel="POS item links" rows={rows} columns={columns} searchPlaceholder="Search StoreHub item, IMS product, size, unit, or rule" />
            )}
          </CardBody>
        </Card>
      </div>

      <Modal variant={ModalVariant.large} isOpen={Boolean(editing)} onClose={closeEditor}>
        <ModalHeader title={editing ? `Edit link ${editing.id}` : "Edit POS item link"} />
        <ModalBody>
          <Form className="pf-toj-form">
            <div className="pf-toj-field-row">
              <FormGroup label="StoreHub item" isRequired>
                <TextInput value={form.item} onChange={(_event, value) => updateForm("item", String(value))} aria-label="StoreHub item" />
              </FormGroup>
              <FormGroup label="Variants">
                <TextInput value={form.variants} onChange={(_event, value) => updateForm("variants", String(value))} aria-label="Variants" />
              </FormGroup>
              <FormGroup label="IMS product" isRequired>
                <TextInput value={form.product} onChange={(_event, value) => updateForm("product", String(value))} aria-label="IMS product" />
              </FormGroup>
              <FormGroup label="Product database name">
                <TextInput value={form.product_dbname} onChange={(_event, value) => updateForm("product_dbname", String(value))} aria-label="Product database name" />
              </FormGroup>
              <FormGroup label="Size">
                <TextInput value={form.size} onChange={(_event, value) => updateForm("size", String(value))} aria-label="Size" />
              </FormGroup>
              <FormGroup label="Unit">
                <TextInput value={form.unit} onChange={(_event, value) => updateForm("unit", String(value))} aria-label="Unit" />
              </FormGroup>
              <FormGroup label="Multiplier">
                <TextInput type="number" value={form.quantity_multiplier} onChange={(_event, value) => updateForm("quantity_multiplier", String(value))} aria-label="Quantity multiplier" />
              </FormGroup>
              <FormGroup label="Rule">
                <FormSelect value={form.quantity_rule} onChange={(_event, value) => updateForm("quantity_rule", String(value))} aria-label="Quantity rule">
                  <FormSelectOption value="multiplier" label="Multiplier" />
                  <FormSelectOption value="base_quantity" label="Base quantity" />
                </FormSelect>
              </FormGroup>
              <FormGroup label="Base quantity">
                <TextInput type="number" value={form.base_quantity} onChange={(_event, value) => updateForm("base_quantity", String(value))} aria-label="Base quantity" />
              </FormGroup>
              <FormGroup label="Review threshold">
                <TextInput type="number" value={form.review_threshold} onChange={(_event, value) => updateForm("review_threshold", String(value))} aria-label="Review threshold" />
              </FormGroup>
              <FormGroup label="Descriptor">
                <TextInput value={form.descriptor} onChange={(_event, value) => updateForm("descriptor", String(value))} aria-label="Descriptor" />
              </FormGroup>
              <FormGroup label="Normalized item">
                <TextInput value={form.normalized_item} onChange={(_event, value) => updateForm("normalized_item", String(value))} aria-label="Normalized item" />
              </FormGroup>
              <FormGroup label="Normalized variants">
                <TextInput value={form.normalized_variants} onChange={(_event, value) => updateForm("normalized_variants", String(value))} aria-label="Normalized variants" />
              </FormGroup>
              <FormGroup label="Active">
                <FormSelect value={form.active} onChange={(_event, value) => updateForm("active", String(value))} aria-label="Active">
                  <FormSelectOption value="1" label="Yes" />
                  <FormSelectOption value="0" label="No" />
                </FormSelect>
              </FormGroup>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={() => void saveMapping()} isLoading={saving} isDisabled={saving}>Save link</Button>
          <Button variant="secondary" onClick={closeEditor}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </PageSection>
  );
}
